import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { NavigationEntry, entryPathOf } from './navigation';
import { SidebarStateService } from './sidebar-state.service';
import { TranslatePipe } from 'eshava-shared';
import { Icon } from '@portal/shared/ui/icon/icon';

/**
 * The navigation, entirely in the sidebar -- there is no menu in the top bar.
 *
 * Five areas stay fully visible, and their screens sit underneath them -- **nothing expands and
 * nothing collapses**. For technical power users that is the best available arrangement: five groups
 * of two to four entries fit without hunting, and an area that has to be opened before its contents
 * can be seen costs a click on every visit.
 *
 * The area link itself goes to the first screen (`entryPathOf`), so it never lands on an empty frame
 * -- which also makes it the thing to click while the sidebar is collapsed, where the labels are gone
 * and with them the children.
 *
 * **Clicking an area while collapsed opens the sidebar.** Otherwise that click reaches the area's
 * first screen and nothing else: the children are hidden with the labels, so the other screens of
 * that area have no way in. Navigating and then finding no way on is worse than not navigating.
 * The alternative -- smaller icons for the children -- would keep it closed at the cost of a second
 * icon set for entries that have no obvious glyph ("Portal users" versus "Invitations"), which is
 * how an icon set stops meaning anything.
 *
 * **Account-wide areas are pinned to the bottom** rather than sitting in the same run as the product
 * areas. They are a different kind of thing -- nothing there depends on the product selected above --
 * and the gap says that more clearly than any label. The split comes from `pinned`, so
 * nothing has to be kept in sync by hand.
 *
 * The active entry is marked by colour and a bar, not by a heavier icon: four of the five icons have
 * no regular variant in Font Awesome Free, so a style switch is not available here.
 */
@Component({
  selector: 'portal-side-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, Icon, TranslatePipe],
  template: `
    <nav class="side-nav" [class.side-nav--collapsed]="sidebar.isCollapsed()">
      <ul class="side-nav__list">
        @for (entry of mainAreas(); track entry.path) {
          <ng-container *ngTemplateOutlet="area; context: { $implicit: entry }" />
        }
      </ul>

      <button
        type="button"
        class="side-nav__toggle"
        [attr.aria-label]="(sidebar.isCollapsed() ? 'shell.sidebar.expand' : 'shell.sidebar.collapse') | t"
        [attr.aria-expanded]="!sidebar.isCollapsed()"
        (click)="sidebar.toggle()"
      >
        <portal-icon name="chevron-down" size="sm" [class.side-nav__toggle-icon--collapsed]="sidebar.isCollapsed()" />
      </button>

      <ul class="side-nav__list side-nav__list--pinned">
        @for (entry of pinnedAreas(); track entry.path) {
          <ng-container *ngTemplateOutlet="area; context: { $implicit: entry }" />
        }
      </ul>
    </nav>

    <ng-template #area let-entry>
      <li>
        <a
          class="side-nav__link"
          [routerLink]="pathOf(entry)"
          routerLinkActive="side-nav__link--active"
          [routerLinkActiveOptions]="{ exact: false }"
          [attr.title]="sidebar.isCollapsed() ? (entry.titleKey | t) : null"
          (click)="sidebar.expand()"
        >
          <portal-icon [name]="entry.icon" />
          <span class="side-nav__label">{{ entry.titleKey | t }}</span>
        </a>

        @if (entry.children && !sidebar.isCollapsed()) {
          <ul class="side-nav__children">
            @for (child of entry.children; track child.path) {
              <li>
                <a
                  class="side-nav__child"
                  [routerLink]="[entry.path, child.path]"
                  routerLinkActive="side-nav__child--active"
                >
                  {{ child.titleKey | t }}
                </a>
              </li>
            }
          </ul>
        }
      </li>
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
    }

    .side-nav {
      /*
       * Stated here rather than relied on from a reset.
       *
       * A height of 100% together with a padding needs border-box, and a package component must not
       * break because a consumer has no reset. Sentinel had none: this box came out 24 pixels taller
       * than its container -- exactly its own vertical padding -- and the shell cut the overflow off,
       * so the last entry looked stuck to the bottom of the screen.
       */
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      width: var(--sidebar-width);
      height: 100%;
      padding: var(--space-3) var(--space-2);
      background-color: var(--surface);
      border-right: 1px solid var(--border);
      transition: width var(--duration) var(--ease);
    }

    .side-nav--collapsed {
      width: var(--sidebar-width-collapsed);
    }

    .side-nav__list {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
      padding: 0;
      margin: 0;
      list-style: none;
    }

    /* The collapse control belongs to the sidebar, not to the top bar: it is the sidebar's own state,
       and the bar now carries the brand. Pushed to the bottom with the account-wide areas. */
    .side-nav__toggle {
      display: flex;
      align-items: center;
      /*
       * Expanded, the chevron stands in the same column as the area icons -- same padding, same icon
       * box. Centred in the full width it read as broken, because nothing else in the sidebar sits
       * there and the eye has a vertical line of icons to compare it against.
       */
      justify-content: flex-start;
      min-height: 2.25rem;
      padding: 0 var(--space-3);
      margin-top: auto;
      color: var(--text-subtle);
      background: none;
      border: 0;
      border-radius: var(--radius-sm);
      cursor: pointer;
    }

    /* Collapsed there is no column to line up with, and centring is what the links do too. */
    .side-nav--collapsed .side-nav__toggle {
      justify-content: center;
      padding: 0;
    }

    .side-nav__toggle:hover {
      color: var(--text);
      background-color: var(--surface-raised);
    }

    /*
     * One drawn chevron, rotated -- rather than two files that could drift apart.
     *
     * **The wrapper is what to select.** portal-icon is what stands in the template; the package
     * element is one level further in, and under emulated encapsulation this rule could not reach
     * it anyway. Written the other way round it matched nothing, so the chevron kept pointing down
     * while expanded -- and looked right while collapsed, because that state is selected by a class
     * on the wrapper itself. Half of it working hid the other half. Found in Sentinel, which had
     * inherited the line from here.
     */
    .side-nav__toggle portal-icon {
      /* The same box as an area icon, so the two are centred on one line rather than merely sharing
         a left edge -- a smaller glyph flush left still looks off by a couple of pixels. */
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--icon-md);
      transform: rotate(90deg);
      transition: transform var(--duration) var(--ease);
    }

    .side-nav__toggle .side-nav__toggle-icon--collapsed {
      transform: rotate(-90deg);
    }

    .side-nav__list--pinned {
      padding-top: var(--space-3);
      padding-bottom: var(--space-3);
      border-top: 1px solid var(--border);
    }

    .side-nav__link {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--space-3);
      /* 44px minimum height: these are the entries most likely to be tapped. */
      min-height: 2.75rem;
      padding: 0 var(--space-3);
      color: var(--text-muted);
      text-decoration: none;
      border-radius: var(--radius-sm);
      white-space: nowrap;
      transition: color var(--duration) var(--ease), background-color var(--duration) var(--ease);
    }

    .side-nav__link:hover {
      color: var(--text);
      background-color: var(--surface-raised);
      text-decoration: none;
    }

    .side-nav__link--active {
      color: var(--accent);
      background-color: var(--accent-soft);
    }

    .side-nav__link--active::before {
      position: absolute;
      top: 20%;
      bottom: 20%;
      left: 0;
      width: 3px;
      content: '';
      background-color: var(--accent);
      border-radius: var(--radius-full);
    }

    .side-nav--collapsed .side-nav__label {
      display: none;
    }

    .side-nav__link:focus-visible,
    .side-nav__child:focus-visible,
    .side-nav__toggle:focus-visible {
      outline: 2px solid var(--focus-ring, #4a91e2);
      outline-offset: -2px;
    }

    .side-nav--collapsed .side-nav__link {
      justify-content: center;
      padding: 0;
    }

    /* Indented to line up with the label of the area above, not with its icon: the eye follows the
       text column, and aligning to the icon leaves the children floating under a glyph. */
    .side-nav__children {
      display: flex;
      flex-direction: column;
      padding: 0 0 var(--space-2) calc(var(--space-3) + 1.25rem + var(--space-3));
      margin: 0;
      list-style: none;
    }

    .side-nav__child {
      display: flex;
      align-items: center;
      /* Shorter than an area entry, taller than text: still a comfortable target, without giving a
         four-entry area the height of four areas. */
      min-height: 2rem;
      padding: 0 var(--space-2);
      font-size: var(--text-sm);
      color: var(--text-subtle);
      text-decoration: none;
      border-radius: var(--radius-sm);
      white-space: nowrap;
    }

    .side-nav__child:hover {
      color: var(--text);
      background-color: var(--surface-raised);
      text-decoration: none;
    }

    .side-nav__child--active {
      color: var(--text);
      font-weight: 600;
    }

    @media (pointer: coarse) {
      .side-nav__child {
        min-height: 2.75rem;
      }
    }
  `
})
export class SideNav {
  protected readonly sidebar = inject(SidebarStateService);

  /** The areas, in the order they are shown. See `NavigationEntry` for why the list is the application's. */
  readonly entries = input.required<readonly NavigationEntry[]>();

  protected readonly mainAreas = computed(() => this.entries().filter(entry => entry.pinned !== true));
  protected readonly pinnedAreas = computed(() => this.entries().filter(entry => entry.pinned === true));

  /** The area link goes to its first screen, so it never opens an empty frame. */
  protected pathOf(entry: NavigationEntry): string {
    return entryPathOf(entry);
  }
}